/**
 * Starter templates for a new event: a suggested name and a timeline to edit. Constants only,
 * not in the database. Step names go into a one-segment GSM-7 text after the event name, so
 * they stay short and plain (no emoji); test/templates.test.ts checks every one fits. Notify
 * marks the moments people want a text for; the in-between steps are Quiet (page only).
 * The web app picks an icon per id.
 *
 * Visibility: a template's timeline is a plan people expect to see, so its steps are public
 * (on followers' timelines from the start, as every step was before visibility existed),
 * except "Having a baby": its steps are medical and unpredictable, so each one stays hidden
 * until it is sent.
 */
export type TemplateStep = { title: string; notify: boolean; hidden?: boolean }

export type EventTemplate = {
  id: 'moving' | 'flight' | 'wedding' | 'home-sale' | 'baby'
  /** What the chip says. */
  label: string
  /** The event name it suggests. */
  title: string
  steps: readonly TemplateStep[]
}

const notify = (title: string): TemplateStep => ({ title, notify: true })
const quiet = (title: string): TemplateStep => ({ title, notify: false })
/** Hidden until sent: off followers' timelines until it happens. */
const hidden = (step: TemplateStep): TemplateStep => ({ ...step, hidden: true })

export const EVENT_TEMPLATES: readonly EventTemplate[] = [
  {
    id: 'moving',
    label: 'Moving day',
    title: 'Moving day',
    steps: [
      quiet('Packing done'),
      notify('Movers arrived'),
      notify('Truck loaded'),
      quiet('On the road'),
      notify('Arrived at the new place'),
      quiet('Truck unloaded'),
      notify('All moved in'),
    ],
  },
  {
    id: 'flight',
    label: 'Flight / trip',
    title: 'Our trip',
    steps: [
      quiet('Left for the airport'),
      quiet('Through security'),
      notify('Boarded'),
      quiet('Took off'),
      notify('Landed'),
      quiet('Got our bags'),
      notify('Arrived'),
    ],
  },
  {
    id: 'wedding',
    label: 'Wedding day',
    title: 'Wedding day',
    steps: [
      quiet('Getting ready'),
      quiet('Photos started'),
      notify('Ceremony starting'),
      notify('Just married'),
      notify('Reception started'),
      quiet('First dance'),
      quiet('Dinner served'),
      notify('Send-off'),
    ],
  },
  {
    id: 'home-sale',
    label: 'Home sale closing',
    title: 'Home sale closing',
    steps: [
      quiet('Final walkthrough done'),
      quiet('At the title office'),
      notify('Signing started'),
      quiet('Papers signed'),
      notify('Funds received'),
      notify('Keys handed over'),
    ],
  },
  {
    id: 'baby',
    label: 'Having a baby',
    title: 'Baby on the way',
    steps: [
      hidden(notify('Heading to the hospital')),
      hidden(quiet('Settled in')),
      hidden(quiet('Things are moving')),
      hidden(notify('Baby is here!')),
      hidden(quiet('Resting and doing well')),
      hidden(notify('Heading home')),
    ],
  },
]
